/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormState } from '../../../container/forms/types/core/form-state.types';
import { createLogger } from '../../../../utils/logger';
import { snakeToCamel, transformFieldValue } from './utils';

const logger = createLogger('WorkflowMapper');

/**
 * Map snake_case API fields into form state (camelCase), applying transformFieldValue.
 * Skips keys listed in skipFields.
 */
export const mapFieldsToFormState = (
  fields: Record<string, any>,
  formState: FormState,
  skipFields: string[] = []
): void => {
  Object.entries(fields).forEach(([fieldName, value]) => {
    if (skipFields.includes(fieldName)) return;
    const camelCaseFieldName = snakeToCamel(fieldName);
    const transformedValue = transformFieldValue(fieldName, value);
    logger.debug('Mapping field to form state', {
      fieldName,
      camelCaseFieldName,
      originalValue: value,
      transformedValue,
    });
    formState.data[camelCaseFieldName] = transformedValue;
  });
}

/**
 * Build display label for selected client: "clientName (clientCode)".
 */
export const buildSelectedClientLabel = (
  clientName: unknown,
  clientCode: unknown
): string => {
  const name =
    typeof clientName === 'string' ? clientName.trim() : '';
  const code =
    typeof clientCode === 'string' ? clientCode.trim() : '';

  // Strip a previously composed suffix so we don't nest labels
  const bareName = name.replace(/\s*-\s*\([^)]*\)\s*$/, '').trim() || name;

  if (bareName && code) {
    return `${bareName} (${code})`;
  }
  return bareName || code;
};

/**
 * Map API client_code/matter_code (or client_matter_number) into form state's clientMatterNumber,
 * and set selectedClientLabel as "clientName (clientCode)".
 */
export const handleClientMatterNumberToForm = (
  sourceFields: Record<string, any>,
  targetFormState: FormState
): void => {
  const clientCode = sourceFields.client_code;
  const matterCode = sourceFields.matter_code;
  const clientMatterNumber = sourceFields.client_matter_number;
  const clientName = sourceFields.client_name || sourceFields.client;

  if (clientCode && matterCode) {
    targetFormState.data.clientMatterNumber = `${clientCode} - ${matterCode}`;
  } else if (clientCode) {
    targetFormState.data.clientMatterNumber = clientCode;
  } else if (matterCode) {
    targetFormState.data.clientMatterNumber = matterCode;
  } else if (clientMatterNumber) {
    targetFormState.data.clientMatterNumber = clientMatterNumber;
  }

  const selectedClientLabel = buildSelectedClientLabel(clientName, clientCode);
  if (selectedClientLabel) {
    targetFormState.data.selectedClientLabel = selectedClientLabel;
  }
}

/**
 * Split form state clientMatterNumber back to API client_code/matter_code or client_matter_number.
 */
export const splitClientMatterNumberForApi = (
  formStateData: FormState['data']
): Record<string, any> => {
  const clientMatterNumber = formStateData.clientMatterNumber;
  if (
    typeof clientMatterNumber === 'string' &&
    clientMatterNumber.includes('/')
  ) {
    const parts = clientMatterNumber.split('/');
    if (parts.length >= 2) {
      return { client_code: parts[0], matter_code: parts[1] };
    }
    return { client_matter_number: clientMatterNumber };
  }
  if (
    typeof clientMatterNumber === 'string' &&
    clientMatterNumber.includes(' - ')
  ) {
    const parts = clientMatterNumber.split(' - ');
    if (parts.length >= 2) {
      return { client_code: parts[0], matter_code: parts[1] };
    }
    return { client_matter_number: clientMatterNumber };
  }
  return { client_matter_number: clientMatterNumber };
};

/** @deprecated Use splitClientMatterNumberForApi */
export const handleClientMatterNumberFromForm = splitClientMatterNumberForApi;

const parseDisplayNameWithCode = (
  value: unknown
): { name: string; code: string } => {
  if (typeof value !== 'string' || !value.trim()) {
    return { name: '', code: '' };
  }

  const trimmed = value.trim();

  // "Name - (CODE)" — used when loading from API
  const dashMatch = trimmed.match(/^(.*?)\s*-\s*\(([^)]+)\)\s*$/);
  if (dashMatch) {
    return { name: dashMatch[1].trim(), code: dashMatch[2].trim() };
  }

  // "Name (CODE)" — used by selectedClientLabel
  const parenMatch = trimmed.match(/^(.*)\s*\(([^)]+)\)\s*$/);
  if (parenMatch) {
    return { name: parenMatch[1].trim(), code: parenMatch[2].trim() };
  }

  return { name: trimmed, code: '' };
};

/**
 * Build client/matter API fields for Payment From Office Account submit.
 * Sends client, client_code, matter, matter_code so the API can persist and return them.
 */
export const buildOfficeAccClientMatterApiFields = (
  formStateData: FormState['data']
): Record<string, string> => {
  const fromNumber = splitClientMatterNumberForApi(formStateData);

  // Codes split from clientMatterNumber. On new selections this holds UNOs, so it
  // is used only as a fallback (e.g. when loading an existing request).
  const numberClientCode =
    typeof fromNumber.client_code === 'string' ? fromNumber.client_code : '';
  const numberMatterCode =
    typeof fromNumber.matter_code === 'string' ? fromNumber.matter_code : '';

  const clientSource =
    formStateData.selectedClientLabel ||
    formStateData.clientName ||
    formStateData.client;
  const matterSource = formStateData.matterName || formStateData.matter;

  const { name: clientName, code: parsedClientCode } =
    parseDisplayNameWithCode(clientSource);
  const { name: matterName, code: parsedMatterCode } =
    parseDisplayNameWithCode(matterSource);

  // Codes explicitly stored when the user selects a client/matter (real codes,
  // e.g. "MADM") take priority over the UNO-based clientMatterNumber value.
  const storedClientCode =
    typeof formStateData.clientCode === 'string' ? formStateData.clientCode : '';
  const storedMatterCode =
    typeof formStateData.matterCode === 'string' ? formStateData.matterCode : '';

  const clientCode = storedClientCode || numberClientCode || parsedClientCode;
  const matterCode = storedMatterCode || numberMatterCode || parsedMatterCode;

  const fields: Record<string, string> = {};
  const clientUno =
    typeof formStateData.clientUno === 'string' ? formStateData.clientUno : '';
  const matterUno =
    typeof formStateData.matterUno === 'string' ? formStateData.matterUno : '';

  if (clientName) fields.client = clientName;
  if (clientCode) fields.client_code = clientCode;
  if (matterName) fields.matter = matterName;
  if (matterCode) fields.matter_code = matterCode;
  if (clientCode) fields.clientCode = clientCode;
  if (matterCode) fields.matterCode = matterCode;
  if (clientUno) {
    fields.client_uno = clientUno;
    fields.clientUno = clientUno;
  }
  if (matterUno) {
    fields.matter_uno = matterUno;
    fields.matterUno = matterUno;
  }

  if (
    !clientCode &&
    !matterCode &&
    typeof fromNumber.client_matter_number === 'string' &&
    fromNumber.client_matter_number
  ) {
    fields.client_matter_number = fromNumber.client_matter_number;
  }

  return fields;
};

/**
 * Split debit/credit clientMatterNumber form values into API code fields for
 * the Transfer Requisition workflow.
 *
 * Each value is either "clientCode - matterCode" (→ *_client_code / *_matter_code)
 * or a single value (→ *_client_matter_number). Non-string values are skipped.
 */
export const buildDebitCreditClientMatterApiFields = (
  formStateData: FormState['data']
): Record<string, string> => {
  const splitPair = (
    value: unknown,
    prefix: 'debit' | 'credit'
  ): Record<string, string> => {
    if (typeof value !== 'string') return {};
    if (value.includes(' - ')) {
      const parts = value.split(' - ');
      if (parts.length >= 2) {
        return {
          [`${prefix}_client_code`]: parts[0],
          [`${prefix}_matter_code`]: parts[1],
        };
      }
      return { [`${prefix}_client_matter_number`]: value };
    }
    return { [`${prefix}_client_matter_number`]: value };
  };

  return {
    ...splitPair(formStateData.debitClientMatterNumber, 'debit'),
    ...splitPair(formStateData.creditClientMatterNumber, 'credit'),
  };
};

/**
 * Format a name with optional code as "Name - (CODE)" to match the client/matter picker.
 */
export const buildNameWithCodeLabel = (
  name: unknown,
  code: unknown
): string => {
  const nameStr = typeof name === 'string' ? name.trim() : '';
  const codeStr = typeof code === 'string' ? code.trim() : '';
  if (!nameStr) return '';
  if (/\s*-\s*\([^)]+\)\s*$/.test(nameStr)) return nameStr;
  if (codeStr) return `${nameStr} - (${codeStr})`;
  return nameStr;
};

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim() !== '';

/**
 * Map Transfer Requisition payment-details API fields into form display state:
 * - debit_client_name / debit_matter_name → debitSelectedClientLabel / debitSelectedMatterLabel
 * - debit_bill_number / credit_bill_number presence → Bill required? checkboxes
 */
export const applyTransferRequisitionPaymentDetailsToForm = (
  sourceFields: Record<string, any>,
  targetFormState: FormState
): void => {
  const debitClientLabel = buildNameWithCodeLabel(
    sourceFields.debit_client_name,
    sourceFields.debit_client_code
  );
  if (debitClientLabel) {
    targetFormState.data.debitSelectedClientLabel = debitClientLabel;
  }

  const debitMatterLabel = buildNameWithCodeLabel(
    sourceFields.debit_matter_name,
    sourceFields.debit_matter_code
  );
  if (debitMatterLabel) {
    targetFormState.data.debitSelectedMatterLabel = debitMatterLabel;
  }

  // API has no bill-required boolean — derive from bill number presence
  if (isNonEmptyString(sourceFields.debit_bill_number)) {
    targetFormState.data.debitShowBillNumber = 'yes';
  }
  if (isNonEmptyString(sourceFields.credit_bill_number)) {
    targetFormState.data.creditShowBillNumber = 'yes';
  }
};

/**
 * Resolve a Transfer Requisition client/matter name for API submit.
 * Prefers the stored raw name; falls back to parsing a "Name - (CODE)" label.
 */
export const resolveTransferRequisitionNameForApi = (
  rawName: unknown,
  selectedLabel: unknown
): string | undefined => {
  if (isNonEmptyString(rawName)) return rawName.trim();
  const { name } = parseDisplayNameWithCode(selectedLabel);
  return name || undefined;
};

/** Normalize section grid data into formState.data.gridData ({ data: rows } or null). */
export const mapGridData = (
  sectionGridData: any,
  formState: FormState
): void => {
  if (sectionGridData && typeof sectionGridData === 'object') {
    if (Array.isArray(sectionGridData)) {
      formState.data.gridData = { data: sectionGridData };
    } else if (
      sectionGridData.data &&
      Array.isArray(sectionGridData.data)
    ) {
      formState.data.gridData = sectionGridData;
    } else {
      formState.data.gridData = null;
    }
  } else {
    formState.data.gridData = null;
  }
}

/** Merge section documents into form state data. */
export const mapDocuments = (
  sectionDocuments: any,
  formState: FormState
): void => {
  if (sectionDocuments) {
    Object.assign(formState.data, sectionDocuments);
  }
}
